import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTenantSlug, buildTenantUrl } from '../config/api';

interface InstitutionRouteProps {
  children: React.ReactNode;
}

const MANAGEMENT_ROLES = ['institution_admin', 'institution_sub_admin', 'teacher'];

// Default ON: institute management is restricted to the tenant subdomain.
// Toggle off via `REACT_APP_RESTRICT_INSTITUTE_MGMT_TO_SUBDOMAIN=false` if
// admins need to manage from platform context too. Platform admins always
// keep cross-tenant freedom regardless of this flag.
const RESTRICT_TO_SUBDOMAIN =
  process.env.REACT_APP_RESTRICT_INSTITUTE_MGMT_TO_SUBDOMAIN !== 'false';

const InstitutionRoute: React.FC<InstitutionRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const tenantSlug = getTenantSlug();
  // Routes wrapped by this guard:
  //   /institution                     → InstitutionSelector  (id is undefined)
  //   /institution/:id                 → InstitutionHome
  //   /institution/:id/groups/:groupId → InstitutionGroupDetail
  const { id: routeInstitutionId } = useParams<{ id?: string }>();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // ── Tenant subdomain: lock the institute pages to the slug's institute ──
  // The subdomain IS the tenant context, so an institute admin/sub-admin on
  // jspt.localhost should only see JSPT's management view — never the
  // selector listing other institutes they happen to belong to, never another
  // institute's pages by hand-typed URL.
  //
  // Platform admins keep their cross-tenant moderation freedom: if they have
  // no membership in the slug's institute, we fall through to the platform
  // logic below so they can still see the selector.
  if (tenantSlug) {
    const slugMembership = user.institution_memberships?.find(
      m => m.institution_slug === tenantSlug
    );
    const hasManagementInSlug = Boolean(
      slugMembership && MANAGEMENT_ROLES.includes(slugMembership.role)
    );

    // Non-admins must have a management role in *this* institute. Members of
    // a different institute (or students on this one) get bounced.
    if (!user.is_admin && !hasManagementInSlug) {
      return <Navigate to="/dashboard" replace />;
    }

    // Once we know the slug's institute id, redirect away from the selector
    // and fix up wrong-id URLs to the correct one.
    const correctId = slugMembership?.institution_id;
    if (correctId) {
      if (!routeInstitutionId) {
        return <Navigate to={`/institution/${correctId}`} replace />;
      }
      if (routeInstitutionId !== correctId) {
        return <Navigate to={`/institution/${correctId}`} replace />;
      }
    }
    return <>{children}</>;
  }

  // ── Platform context ──
  if (user.is_admin) return <>{children}</>;

  const memberships = user.institution_memberships ?? [];
  const hasAccess = memberships.some(m => MANAGEMENT_ROLES.includes(m.role));
  if (!hasAccess) return <Navigate to="/dashboard" replace />;

  // Subdomain-restriction flag: institute admins / sub-admins / teachers
  // hitting institute pages on platform context get bounced to their
  // tenant subdomain. Single-mental-model "tenant scope = subdomain" UX,
  // and matches how Book.institution_id is only stamped on subdomain
  // uploads (so platform context = personal/platform context, period).
  if (RESTRICT_TO_SUBDOMAIN) {
    // Pick the right slug: prefer the membership matching :id in the URL,
    // fall back to the user's first management membership if URL has none.
    const targetMembership = routeInstitutionId
      ? memberships.find(m =>
          m.institution_id === routeInstitutionId
          && MANAGEMENT_ROLES.includes(m.role))
      : memberships.find(m => MANAGEMENT_ROLES.includes(m.role));

    if (targetMembership?.institution_slug) {
      const path = `${window.location.pathname}${window.location.search}`;
      const targetUrl = buildTenantUrl(targetMembership.institution_slug, path);
      if (targetUrl) {
        // Hard navigation — different host. Use replace so the back button
        // doesn't leave a redirect loop entry in history.
        window.location.replace(targetUrl);
        return null;
      }
      // buildTenantUrl returned null (raw IP / unrecognized host) — fall
      // through and render. Better to render than to wedge the user.
    }
  }

  return <>{children}</>;
};

export default InstitutionRoute;
